import { ActivityMetadataModel, FlowInstanceStatus, FlowInstanceStepModel, StepType } from '../../../api/api';
import { FlowExecutionState, FlowExecutionTrace, mergeFlowExecutionState } from '../Components/Contexts/FlowExecutionTraceContext';
import { ActivityCategories, ActivityTypes } from '../Models/ActivityConstants';
import { AGENT_TOOL_TRANSITION_TYPE } from '../Models/FlowModel';
import { FlowEdge, FlowNode } from '../Models/GraphModel';

export type ExecutionTraceStep = Pick<FlowInstanceStepModel, 'activityId' | 'executionId' | 'parentActivityId' | 'timestamp' | 'type' | 'order' | 'name'> & {
	executionRunIndex?: number;
	runId?: string;
};

export function isActiveFlowInstanceStatus(status: FlowInstanceStatus | undefined) {
	return status === FlowInstanceStatus.Running || status === FlowInstanceStatus.Waiting;
}

export function isFinishedFlowInstanceStatus(status: FlowInstanceStatus | undefined) {
	return status === FlowInstanceStatus.Completed || status === FlowInstanceStatus.Failed;
}

export function getEdgeBetweenActivities(edges: FlowEdge[], sourceId: string | undefined, targetId: string | undefined) {
	if (!sourceId || !targetId) {
		return undefined;
	}

	return edges.find((edge) => edge.source === sourceId && edge.target === targetId);
}

export function getEdgeConnectingActivities(edges: FlowEdge[], firstActivityId: string | undefined, secondActivityId: string | undefined) {
	return getEdgeBetweenActivities(edges, firstActivityId, secondActivityId) ?? getEdgeBetweenActivities(edges, secondActivityId, firstActivityId);
}

export function getExecutionStepKey(step: ExecutionTraceStep) {
	return `${step.runId ?? step.executionRunIndex ?? 'live'}:${step.executionId}:${step.activityId}:${step.type}`;
}

export function compareExecutionSteps(left: ExecutionTraceStep, right: ExecutionTraceStep) {
	const leftRunIndex = left.executionRunIndex ?? Number.MAX_SAFE_INTEGER;
	const rightRunIndex = right.executionRunIndex ?? Number.MAX_SAFE_INTEGER;

	return leftRunIndex - rightRunIndex || left.order - right.order || new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime();
}

export function startsNewWaitingRun(
	step: ExecutionTraceStep,
	previousStep: ExecutionTraceStep | undefined,
	selectedInstanceStatus: FlowInstanceStatus | undefined,
) {
	return step.type === StepType.OnEnter && previousStep?.type === StepType.OnLeave && selectedInstanceStatus === FlowInstanceStatus.Waiting;
}

export function getCurrentSignalRRunSteps(steps: ExecutionTraceStep[], edges: FlowEdge[]) {
	let currentRunStartIndex = 0;

	for (let index = 1; index < steps.length; index += 1) {
		const previousStep = steps[index - 1];
		const step = steps[index];
		if (step.runId && previousStep.runId) {
			if (step.runId !== previousStep.runId) {
				currentRunStartIndex = index;
			}
			continue;
		}

		if (
			step.type === StepType.OnEnter &&
			previousStep.type === StepType.OnLeave &&
			!getEdgeConnectingActivities(edges, previousStep.activityId, step.activityId)
		) {
			currentRunStartIndex = index;
		}
	}

	return steps.slice(currentRunStartIndex);
}

export function getLatestPersistedRunSteps(steps: ExecutionTraceStep[]) {
	if (steps.length === 0) {
		return [];
	}

	const sortedSteps = [...steps].sort(compareExecutionSteps);
	const latestStep = sortedSteps[sortedSteps.length - 1];
	if (latestStep.runId) {
		return sortedSteps.filter((step) => step.runId === latestStep.runId);
	}

	const latestRunIndex = steps.reduce((latest, step) => Math.max(latest, step.executionRunIndex ?? Number.MAX_SAFE_INTEGER), Number.MIN_SAFE_INTEGER);
	return sortedSteps.filter((step) => (step.executionRunIndex ?? Number.MAX_SAFE_INTEGER) === latestRunIndex);
}

export function getActivePersistedRunSteps(steps: ExecutionTraceStep[], currentActivityId: string | undefined, currentSignalRRunSteps: ExecutionTraceStep[]) {
	const latestRunSteps = getLatestPersistedRunSteps(steps).sort(compareExecutionSteps);
	if (latestRunSteps.length === 0) {
		return [];
	}

	const signalRRunId = currentSignalRRunSteps.find((step) => step.runId)?.runId;
	if (signalRRunId && !latestRunSteps.some((step) => step.runId === signalRRunId)) {
		return [];
	}

	if (currentSignalRRunSteps.length > 0 && currentActivityId && !latestRunSteps.some((step) => step.activityId === currentActivityId)) {
		return [];
	}

	return latestRunSteps;
}

export function mergeTraceSteps(baseSteps: ExecutionTraceStep[], overlaySteps: ExecutionTraceStep[]) {
	const baseStepKeys = new Set(baseSteps.map(getExecutionStepKey));
	return [...baseSteps, ...overlaySteps.filter((step) => !baseStepKeys.has(getExecutionStepKey(step)))].sort(compareExecutionSteps);
}

export function getGraphOverlaySteps({
	persistedRunSteps,
	signalRSteps,
	edges,
	selectedInstanceStatus,
	selectedInstanceCurrentActivityId,
}: {
	persistedRunSteps: ExecutionTraceStep[];
	signalRSteps: ExecutionTraceStep[];
	edges: FlowEdge[];
	selectedInstanceStatus?: FlowInstanceStatus;
	selectedInstanceCurrentActivityId?: string;
}) {
	const currentSignalRRunSteps = getCurrentSignalRRunSteps([...signalRSteps].sort(compareExecutionSteps), edges);

	if (isActiveFlowInstanceStatus(selectedInstanceStatus)) {
		return mergeTraceSteps(getActivePersistedRunSteps(persistedRunSteps, selectedInstanceCurrentActivityId, currentSignalRRunSteps), currentSignalRRunSteps);
	}

	if (!selectedInstanceStatus && signalRSteps.length > 0) {
		return currentSignalRRunSteps;
	}

	return mergeTraceSteps(persistedRunSteps, signalRSteps);
}

function setTraceState(states: Map<string, FlowExecutionState>, id: string | undefined, state: FlowExecutionState) {
	if (!id) {
		return;
	}

	states.set(id, mergeFlowExecutionState(states.get(id), state));
}

function isToolTraceStep(stepNode: FlowNode | undefined, activityByName: Map<string, ActivityMetadataModel>) {
	const stepMetadata = stepNode ? activityByName.get(stepNode.data.Type) : undefined;

	return !!stepNode?.parentId || stepMetadata?.categories?.includes(ActivityCategories.AGENT_SERVICE) || stepNode?.data.Type === ActivityTypes.GROUP_TOOL;
}

export function buildExecutionTrace(
	steps: ExecutionTraceStep[],
	edges: FlowEdge[],
	nodes: FlowNode[],
	activityByName: Map<string, ActivityMetadataModel>,
	clearActiveTrace: boolean,
	currentActivityId: string | undefined,
	isWaiting = false,
	reportedCurrentActivityId?: string | null,
): FlowExecutionTrace | undefined {
	if (reportedCurrentActivityId !== undefined) {
		currentActivityId = reportedCurrentActivityId ?? undefined;
	}
	if (steps.length === 0 && (!currentActivityId || clearActiveTrace)) {
		return undefined;
	}

	const nodeStates = new Map<string, FlowExecutionState>();
	const edgeStates = new Map<string, FlowExecutionState>();
	const nodeVisitCounts = new Map<string, number>();
	const edgeVisitCounts = new Map<string, number>();
	const nodeById = new Map(nodes.map((node) => [node.id, node]));
	let previousEnteredActivityId: string | undefined;
	let activeNodeId: string | undefined;
	let lastEnteredActivityId: string | undefined;
	let activeEdgeId: string | undefined;
	let previousRunIndex: number | undefined;

	for (const step of steps) {
		if (!step.activityId) {
			continue;
		}

		if (previousRunIndex !== step.executionRunIndex) {
			previousEnteredActivityId = undefined;
			previousRunIndex = step.executionRunIndex;
		}

		setTraceState(nodeStates, step.activityId, 'visited');

		if (step.type === StepType.OnEnter) {
			lastEnteredActivityId = step.activityId;
			nodeVisitCounts.set(step.activityId, (nodeVisitCounts.get(step.activityId) ?? 0) + 1);
			const stepNode = nodeById.get(step.activityId);
			const parentActivityId = step.parentActivityId ?? stepNode?.parentId;
			const callerEdge = getEdgeBetweenActivities(edges, parentActivityId ?? previousEnteredActivityId, step.activityId);
			// A top-level agent can be invoked as a tool without being a graph child
			// or having AgentService metadata. Preserve the caller for its next workflow edge.
			const isToolStep = isToolTraceStep(stepNode, activityByName) || callerEdge?.data?.Type === AGENT_TOOL_TRANSITION_TYPE;
			const edge = getEdgeConnectingActivities(edges, parentActivityId, step.activityId)
				?? (isToolStep
					? getEdgeConnectingActivities(edges, previousEnteredActivityId, step.activityId)
					: getEdgeBetweenActivities(edges, previousEnteredActivityId, step.activityId));

			if (edge) {
				setTraceState(edgeStates, edge.id, 'visited');
				edgeVisitCounts.set(edge.id, (edgeVisitCounts.get(edge.id) ?? 0) + 1);
				activeEdgeId = edge.id;
			}

			if (!isToolStep) {
				previousEnteredActivityId = step.activityId;
			}

			activeNodeId = step.activityId;
		} else if (step.type === StepType.OnLeave && activeNodeId === step.activityId) {
			activeNodeId = undefined;
			activeEdgeId = undefined;
		}
	}

	setTraceState(nodeStates, currentActivityId, 'visited');
	if (currentActivityId && !nodeVisitCounts.has(currentActivityId)) {
		nodeVisitCounts.set(currentActivityId, 1);
	}

	if (!clearActiveTrace) {
		const highlightedActivityId = reportedCurrentActivityId !== undefined
			? currentActivityId : isWaiting ? lastEnteredActivityId ?? currentActivityId : activeNodeId ?? currentActivityId;
		setTraceState(nodeStates, highlightedActivityId, 'active');
		if (reportedCurrentActivityId === undefined || highlightedActivityId === activeNodeId) {
			setTraceState(edgeStates, activeEdgeId, 'active');
		}
	}

	return {
		nodeStates,
		edgeStates,
		nodeVisitCounts,
		edgeVisitCounts,
	};
}
